/**
 * Local storage utility for the Sur Excel Add-in
 * Uses Office Settings API for persistent storage
 */

import { config } from "./config";
import { User } from "../types";

/* global Office */

class StorageService {
  private settings: Office.Settings | null = null;

  /**
   * Initialize the storage service with Office Settings
   */
  initialize(): void {
    if (Office.context && Office.context.document) {
      this.settings = Office.context.document.settings;
    }
  }

  /**
   * Save access token
   */
  setAccessToken(token: string): void {
    this.set(config.storageKeys.accessToken, token);
  }

  /**
   * Get access token
   */
  getAccessToken(): string | null {
    return this.get(config.storageKeys.accessToken);
  }

  /**
   * Save refresh token
   */
  setRefreshToken(token: string): void {
    this.set(config.storageKeys.refreshToken, token);
  }

  /**
   * Get refresh token
   */
  getRefreshToken(): string | null {
    return this.get(config.storageKeys.refreshToken);
  }

  /**
   * Save user data
   */
  setUser(user: User): void {
    this.set(config.storageKeys.user, JSON.stringify(user));
  }

  /**
   * Get user data
   */
  getUser(): User | null {
    const userData = this.get(config.storageKeys.user);
    if (userData) {
      try {
        return JSON.parse(userData) as User;
      } catch {
        return null;
      }
    }
    return null;
  }

  /**
   * Clear all authentication data
   */
  clearAuth(): void {
    this.remove(config.storageKeys.accessToken);
    this.remove(config.storageKeys.refreshToken);
    this.remove(config.storageKeys.user);
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  /**
   * Generic set method
   */
  private set(key: string, value: string): void {
    if (this.settings) {
      this.settings.set(key, value);
      this.settings.saveAsync();
    } else {
      // Fallback to localStorage for development
      try {
        localStorage.setItem(key, value);
      } catch {
        console.warn("Unable to save to storage");
      }
    }
  }

  /**
   * Generic get method
   */
  private get(key: string): string | null {
    if (this.settings) {
      return this.settings.get(key) as string | null;
    } else {
      // Fallback to localStorage for development
      try {
        return localStorage.getItem(key);
      } catch {
        return null;
      }
    }
  }

  /**
   * Generic remove method
   */
  private remove(key: string): void {
    if (this.settings) {
      this.settings.remove(key);
      this.settings.saveAsync();
    } else {
      // Fallback to localStorage for development
      try {
        localStorage.removeItem(key);
      } catch {
        console.warn("Unable to remove from storage");
      }
    }
  }
}

export const storage = new StorageService();
export default storage;
